import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Profile.css';
import { getProfile, updateProfile, uploadProfilePhoto, changePassword, fetchAuthenticatedImage, getMyRoleRequests, requestRole } from '../../api/services/authService';
import Modal from '../../components/Modal/Modal';
import { getProfileInitial } from '../../utils/profileInitial';

// Helper function to get user-specific profile photo storage key
const getProfilePhotoStorageKey = (userId) => {
  return userId ? `tenantscore_profile_photo_url_${userId}` : 'tenantscore_profile_photo_url';
};

const normalizeRole = (role) => {
  const upper = String(role).toUpperCase().replace(/\s+/g, '_');
  if (upper === 'AIRBNB_HOST') return 'BNB_HOST';
  if (upper === 'ADMIN') return 'SYSTEM_ADMIN';
  return upper;
};

const getActiveDashboardRoleFromPath = (pathname) => {
  if (pathname.startsWith('/landlord')) return 'LANDLORD';
  if (pathname.startsWith('/airbnb-host')) return 'BNB_HOST';
  if (pathname.startsWith('/agent')) return 'AGENT';
  if (pathname.startsWith('/tenant')) return 'TENANT';
  if (pathname.startsWith('/system-admin')) return 'SYSTEM_ADMIN';
  return null;
};

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dob: '',
    profilePhotoUrl: ''
  });
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ text: '', type: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState(null);
  const [rejectedRoleRequests, setRejectedRoleRequests] = useState([]);
  const [allRoleRequests, setAllRoleRequests] = useState([]); // Track all role requests to check for pending ones
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [selectedRoleRequest, setSelectedRoleRequest] = useState(null);
  const [resubmitRole, setResubmitRole] = useState('');
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [resubmitMessage, setResubmitMessage] = useState({ text: '', type: '' });

  // Multi-role request (non-admin users only)
  const [showRoleRequestModal, setShowRoleRequestModal] = useState(false);
  const [roleSelections, setRoleSelections] = useState([]); // Array<string> of API roles: LANDLORD | BNB_HOST | AGENT
  const [isSubmittingRoleRequests, setIsSubmittingRoleRequests] = useState(false);
  const [roleRequestMessage, setRoleRequestMessage] = useState({ text: '', type: '' });

  // Check if current user is a system admin
  const isSystemAdmin = useCallback(() => {
    if (!profile || !profile.roles) return false;
    const rolesArray = Array.isArray(profile.roles) ? profile.roles : [];
    return rolesArray.some(role => {
      const roleStr = typeof role === 'string' ? role : (role?.role || role);
      const upperRole = roleStr?.toUpperCase();
      return upperRole === 'SYSTEM_ADMIN' || upperRole === 'ADMIN';
    });
  }, [profile]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (location?.state?.openPasswordModal) {
      setShowPasswordModal(true);
    }
  }, [location?.state?.openPasswordModal]);

  // Only fetch rejected role requests if user is not a system admin
  useEffect(() => {
    if (!profile) return;
    
    const adminCheck = isSystemAdmin();
    console.log('Profile roles:', profile.roles, 'Is admin:', adminCheck); // Debug log
    
    if (!adminCheck) {
      // User is not an admin, fetch their rejected role requests
      fetchRejectedRoleRequests();
    } else {
      // User is an admin, clear any rejected role requests
      setRejectedRoleRequests([]);
    }
  }, [profile, isSystemAdmin]);

  // Fetch authenticated image when profilePhotoPreview changes
  useEffect(() => {
    const loadAuthenticatedImage = async () => {
      if (!profilePhotoPreview) {
        setAuthenticatedImageUrl(null);
        return;
      }

      // If it's already a data URL (from FileReader) or an existing local blob, use it directly
      if (profilePhotoPreview.startsWith('data:') || profilePhotoPreview.startsWith('blob:')) {
        setAuthenticatedImageUrl(profilePhotoPreview);
        return;
      }

      // Fetch authenticated image
      try {
        const blobUrl = await fetchAuthenticatedImage(profilePhotoPreview);
        if (blobUrl) {
          setAuthenticatedImageUrl(blobUrl);
        } else {
          setAuthenticatedImageUrl(null);
        }
      } catch (error) {
        console.error('Failed to load authenticated image:', error);
        setAuthenticatedImageUrl(null);
      }
    };

    loadAuthenticatedImage();

    // Do NOT revoke blob URLs here!
    // The authenticated image fetcher maintains a cache during the session.
    // Revoking it here breaks the cache and causes ERR_FILE_NOT_FOUND.
  }, [profilePhotoPreview]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await getProfile();
      if (response.status === 'success' && response.data) {
        setProfile(response.data);
        
        // Always prioritize API response over localStorage to ensure user-specific data
        // Only use localStorage as a fallback if API doesn't have a photo
        const userId = response.data.id;
        const storageKey = getProfilePhotoStorageKey(userId);
        const apiPhotoUrl = response.data.profilePhotoPath || '';
        
        // Clean up old global localStorage key if it exists (migration from old implementation)
        const oldGlobalKey = 'tenantscore_profile_photo_url';
        if (localStorage.getItem(oldGlobalKey)) {
          localStorage.removeItem(oldGlobalKey);
        }
        
        // Get saved photo from localStorage only if it's for this specific user
        const savedPhotoUrl = userId ? localStorage.getItem(storageKey) : null;
        
        // Use API response first, then localStorage fallback, then empty string
        const photoUrl = apiPhotoUrl || savedPhotoUrl || '';
        
        setEditForm({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          phone: response.data.phone || '',
          dob: response.data.dob || '',
          profilePhotoUrl: photoUrl
        });
        setProfilePhotoPreview(photoUrl || null);
        
        // Update local storage with current profile photo from API if it exists and we have a user ID
        if (apiPhotoUrl && userId) {
          localStorage.setItem(storageKey, apiPhotoUrl);
        } else if (userId) {
          // Clear localStorage for this user if API doesn't have a photo
          localStorage.removeItem(storageKey);
        }
        
        // Return the profile data for immediate use
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectedRoleRequests = async () => {
    try {
      const response = await getMyRoleRequests();
      
      if (response.status === 'success' && response.data) {
        // Response data is an array directly, not paginated
        const requestsArray = Array.isArray(response.data) ? response.data : [];
        
        // Store all role requests to check for pending ones
        setAllRoleRequests(requestsArray);
        
        // Filter for rejected requests that can be resubmitted
        const rejectedRequests = requestsArray.filter(req => {
          const status = req.status?.toUpperCase();
          const canResubmit = req.canResubmit === true;
          return (status === 'REJECTED' || status === 'REJECTED_RESUBMIT_ALLOWED') && canResubmit;
        });
        
        setRejectedRoleRequests(rejectedRequests);
      } else {
        setRejectedRoleRequests([]);
        setAllRoleRequests([]);
      }
    } catch (error) {
      // Handle errors gracefully
      console.error('Failed to fetch rejected role requests:', error);
      setRejectedRoleRequests([]);
      setAllRoleRequests([]);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result;
        setProfilePhotoPreview(previewUrl);
        // Save preview URL to local storage with user-specific key
        if (profile?.id) {
          const storageKey = getProfilePhotoStorageKey(profile.id);
          localStorage.setItem(storageKey, previewUrl);
        }
        setEditForm(prev => ({
          ...prev,
          profilePhotoUrl: previewUrl
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setUpdateMessage({ text: '', type: '' });

    try {
      let profilePhotoUrl = editForm.profilePhotoUrl;

      if (profilePhotoFile && profile?.id) {
        const uploadResponse = await uploadProfilePhoto(profilePhotoFile, profile.id);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          profilePhotoUrl = uploadResponse.data.url;
        } else {
          throw new Error('Failed to upload profile photo');
        }
      }

      const updateData = {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        phone: editForm.phone.trim(),
        dob: editForm.dob,
        ...(profilePhotoUrl && { profilePhotoUrl })
      };

      const response = await updateProfile(updateData);

      if (response.status === 'success' && response.data) {
        setProfile(response.data);
        
        // Update profile photo URL and save to local storage with user-specific key
        const updatedPhotoUrl = response.data.profilePhotoPath || profilePhotoUrl;
        if (updatedPhotoUrl) {
          setEditForm(prev => ({
            ...prev,
            profilePhotoUrl: updatedPhotoUrl
          }));
          setProfilePhotoPreview(updatedPhotoUrl);
          // Save the uploaded URL to local storage with user-specific key
          if (response.data.id) {
            const storageKey = getProfilePhotoStorageKey(response.data.id);
            localStorage.setItem(storageKey, updatedPhotoUrl);
          }
        }
        
        // Clear the file input but keep the preview
        setProfilePhotoFile(null);
        setUpdateMessage({ text: 'Profile updated successfully!', type: 'success' });
        
        setTimeout(() => {
          setShowEditModal(false);
          setUpdateMessage({ text: '', type: '' });
          // Don't clear profilePhotoFile here - image should persist
        }, 1500);
      } else {
        setUpdateMessage({ text: response.message || 'Failed to update profile', type: 'error' });
      }
    } catch (error) {
      console.error('Update profile error:', error);
      setUpdateMessage({ 
        text: error.message || 'Failed to update profile. Please try again.', 
        type: 'error' 
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const formatRole = (roles) => {
    if (!roles || roles.length === 0) return 'Guest';
    // Filter out GUEST role and format remaining roles
    const filteredRoles = roles.filter(role => 
      role.toUpperCase() !== 'GUEST' && role !== 'Guest'
    );
    if (filteredRoles.length === 0) return 'Guest';
    return filteredRoles.map(role => role.replace('_', ' ')).join(', ');
  };

  const formatRoleName = (role) => {
    const roleMap = {
      'LANDLORD': 'Landlord',
      'BNB_HOST': 'BNB Host',
      'AGENT': 'Agent',
      'GUEST': 'Guest',
      'TENANT': 'Tenant'
    };
    return roleMap[role] || role;
  };

  const handleRoleSwitch = (role) => {
    const roleRoutes = {
      'LANDLORD': '/landlord',
      'BNB_HOST': '/airbnb-host',
      'AGENT': '/agent',
      'TENANT': '/tenant',
      'GUEST': '/tenant',
      'ADMIN': '/system-admin',
      'SYSTEM_ADMIN': '/system-admin'
    };

    const route = roleRoutes[normalizeRole(role)] || '/';
    navigate(route);
  };

  const activeDashboardRole = getActiveDashboardRoleFromPath(location.pathname);
  const showDeleteAccount = ['/landlord', '/agent', '/airbnb-host', '/tenant'].some((path) =>
    location.pathname.startsWith(path)
  );

  const handleDeleteAccountNavigation = useCallback(() => {
    if (location.pathname.startsWith('/tenant')) {
      navigate('/tenant/deleteaccount');
    } else if (location.pathname.startsWith('/landlord')) {
      navigate('/landlord/deleteaccount');
    } else if (location.pathname.startsWith('/agent')) {
      navigate('/agent/deleteaccount');
    } else if (location.pathname.startsWith('/airbnb-host')) {
      navigate('/airbnb-host/deleteaccount');
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  // Summarize the latest status for a specific role request.
  // Priority: APPROVED > PENDING/PENDING_REVIEW > REJECTED > NONE
  const getRoleRequestStatusForRole = (role) => {
    const roleUpper = String(role).toUpperCase();
    const requests = Array.isArray(allRoleRequests) ? allRoleRequests : [];

    const roleRequests = requests.filter((req) => {
      const reqRole = String(req?.role ?? req?.requestedRole ?? '').toUpperCase();
      return reqRole === roleUpper;
    });

    if (roleRequests.length === 0) {
      return { status: 'NONE', canResubmit: false };
    }

    const hasApproved = roleRequests.some((req) => String(req?.status ?? '').toUpperCase() === 'APPROVED');
    if (hasApproved) return { status: 'APPROVED', canResubmit: false };

    const hasPending = roleRequests.some((req) => {
      const status = String(req?.status ?? '').toUpperCase();
      return status === 'PENDING' || status === 'PENDING_REVIEW';
    });
    if (hasPending) return { status: 'PENDING', canResubmit: false };

    const hasRejected = roleRequests.some((req) => {
      const status = String(req?.status ?? '').toUpperCase();
      return status === 'REJECTED' || status === 'REJECTED_RESUBMIT_ALLOWED';
    });
    if (hasRejected) {
      const canResubmit = rejectedRoleRequests.some((r) => String(r?.role ?? '').toUpperCase() === roleUpper);
      return { status: 'REJECTED', canResubmit };
    }

    return { status: 'UNKNOWN', canResubmit: false };
  };

  const isSelectableRoleRequest = (role) => {
    const summary = getRoleRequestStatusForRole(role);
    if (summary.status === 'APPROVED' || summary.status === 'PENDING') return false;
    if (summary.status === 'REJECTED') return summary.canResubmit;
    return true;
  };

  // Helper function to check if there's a pending request for a specific role
  const hasPendingRequestForRole = (role) => {
    if (!allRoleRequests || allRoleRequests.length === 0) return false;
    
    return allRoleRequests.some(req => {
      const requestRole = req.role?.toUpperCase();
      const requestStatus = req.status?.toUpperCase();
      const targetRole = role?.toUpperCase();
      
      return requestRole === targetRole && 
             (requestStatus === 'PENDING' || requestStatus === 'PENDING_REVIEW');
    });
  };


  const handleResubmitClick = (roleRequest) => {
    setSelectedRoleRequest(roleRequest);
    setResubmitRole(roleRequest.role);
    setResubmitMessage({ text: '', type: '' });
    setShowResubmitModal(true);
  };


  const handleResubmit = async (e) => {
    e.preventDefault();
    setResubmitMessage({ text: '', type: '' });

    if (!resubmitRole) {
      setResubmitMessage({ text: 'Please select a role', type: 'error' });
      return;
    }

    setIsResubmitting(true);

    try {
      // Refresh profile to ensure we have the latest KYC status
      let currentProfile = profile;
      try {
        const refreshedProfile = await fetchProfile();
        if (refreshedProfile) {
          currentProfile = refreshedProfile;
        }
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }

      // Check if KYC documents are submitted (required before role request)
      if (!currentProfile) {
        setResubmitMessage({ text: 'Profile data not loaded. Please refresh the page.', type: 'error' });
        setIsResubmitting(false);
        return;
      }

      // Check if KYC exists - API requires KYC to be submitted (status can be PENDING, VERIFIED, or APPROVED)
      const kycObject = currentProfile.kyc;
      const kycStatus = currentProfile.kycStatus?.toUpperCase() || '';
      const kycObjectStatus = kycObject?.status?.toUpperCase() || '';
      
      const hasKYC = kycObject && (
        kycObject.idDocumentUrl || 
        kycObject.idDocumentBackUrl || 
        kycObject.selfieUrl
      );
      
      const statusCheck = kycStatus === 'PENDING' || kycStatus === 'VERIFIED' || kycStatus === 'APPROVED';
      const objectIdCheck = !!(kycObject && kycObject.id);
      const objectStatusCheck = !!(hasKYC && (
        kycObjectStatus === 'PENDING' ||
        kycObjectStatus === 'VERIFIED' ||
        kycObjectStatus === 'APPROVED'
      ));
      
      const isKYCSubmitted = statusCheck || objectIdCheck || objectStatusCheck;

      if (!isKYCSubmitted) {
        setResubmitMessage({ 
          text: 'You must submit your KYC documents first before requesting this role. KYC does not need to be approved yet.', 
          type: 'error' 
        });
        setIsResubmitting(false);
        return;
      }

      setResubmitMessage({ text: 'Submitting role request...', type: 'success' });
      const roleResponse = await requestRole(resubmitRole);
      
      if (roleResponse.status !== 'success') {
        throw new Error(roleResponse.message || 'Failed to submit role request');
      }

      setResubmitMessage({ 
        text: 'Role request submitted successfully!', 
        type: 'success' 
      });

      // Refresh data and close modal
      setTimeout(async () => {
        await fetchRejectedRoleRequests();
        await fetchProfile();
        setShowResubmitModal(false);
        setResubmitMessage({ text: '', type: '' });
      }, 2000);

    } catch (error) {
      console.error('Resubmit error:', error);
      let errorMessage = error.message || 'Failed to process resubmit. Please try again.';
      setResubmitMessage({ 
        text: errorMessage, 
        type: 'error' 
      });
    } finally {
      setIsResubmitting(false);
    }
  };

  const resetRoleRequestModalState = () => {
    setRoleSelections([]);
    setRoleRequestMessage({ text: '', type: '' });
  };

  const handleRoleRequestsSubmit = async (e) => {
    e.preventDefault();
    setRoleRequestMessage({ text: '', type: '' });

    if (!Array.isArray(roleSelections) || roleSelections.length === 0) {
      setRoleRequestMessage({ text: 'Please select at least one role', type: 'error' });
      return;
    }

    setIsSubmittingRoleRequests(true);

    try {
      // Refresh profile to reduce chance of stale KYC status
      let currentProfile = profile;
      try {
        const refreshedProfile = await fetchProfile();
        if (refreshedProfile) currentProfile = refreshedProfile;
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }

      if (!currentProfile) {
        setRoleRequestMessage({ text: 'Profile data not loaded. Please refresh the page.', type: 'error' });
        return;
      }

      // KYC check (API requires KYC to be submitted before role requests)
      const kycObject = currentProfile.kyc;
      const kycStatus = currentProfile.kycStatus?.toUpperCase() || '';
      const kycObjectStatus = kycObject?.status?.toUpperCase() || '';
      const hasKYC = !!kycObject && !!(kycObject.idDocumentUrl || kycObject.idDocumentBackUrl || kycObject.selfieUrl);

      const statusCheck = kycStatus === 'PENDING' || kycStatus === 'VERIFIED' || kycStatus === 'APPROVED';
      const objectIdCheck = !!(kycObject && kycObject.id);
      const objectStatusCheck = !!(
        hasKYC &&
        (kycObjectStatus === 'PENDING' || kycObjectStatus === 'VERIFIED' || kycObjectStatus === 'APPROVED')
      );

      const isKYCSubmitted = statusCheck || objectIdCheck || objectStatusCheck;

      if (!isKYCSubmitted) {
        setRoleRequestMessage({
          text: 'You must submit your KYC documents first before requesting roles. KYC does not need to be approved yet.',
          type: 'error'
        });
        return;
      }

      // Submit role requests (one call per role)
      for (const role of roleSelections) {
        await requestRole(role);
      }

      setRoleRequestMessage({ text: 'Role request(s) submitted successfully!', type: 'success' });

      // Refresh lists and close the modal after a short delay
      setTimeout(async () => {
        resetRoleRequestModalState();
        setShowRoleRequestModal(false);
        try {
          await fetchRejectedRoleRequests();
          await fetchProfile();
        } catch (error) {
          console.error('Failed to refresh after role requests:', error);
        }
      }, 800);
    } catch (error) {
      console.error('Role request submission error:', error);
      let errorMessage = error?.message || 'Failed to submit role request(s). Please try again.';

      if (
        errorMessage.toLowerCase().includes('role') &&
        (errorMessage.toLowerCase().includes('kyb') ||
          errorMessage.toLowerCase().includes('landlord') ||
          errorMessage.toLowerCase().includes('bnb_host') ||
          errorMessage.toLowerCase().includes('role-requests'))
      ) {
        errorMessage = 'KYB is only for Landlords and short stay bnb owners. You must register as LANDLORD or BNB_HOST';
      }

      setRoleRequestMessage({ text: errorMessage, type: 'error' });
    } finally {
      setIsSubmittingRoleRequests(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);
    setPasswordMessage({ text: '', type: '' });

    try {
      const response = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);

      if (response.status === 'success') {
        setPasswordMessage({ text: response.message || 'Password updated successfully!', type: 'success' });
        setPasswordForm({
          currentPassword: '',
          newPassword: ''
        });
        
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordMessage({ text: '', type: '' });
        }, 1500);
      } else {
        setPasswordMessage({ text: response.message || 'Failed to change password', type: 'error' });
      }
    } catch (error) {
      console.error('Change password error:', error);
      setPasswordMessage({ 
        text: error.message || 'Failed to change password. Please try again.', 
        type: 'error' 
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container">
        <div className="profile-error">Failed to load profile</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* My Profile Section */}
      <div className="profile-summary-card">
        <h2 className="profile-section-title">My Profile</h2>
        <div className="profile-summary-content">
          <div className="profile-picture-container">
            <div className="profile-picture-wrapper">
              {authenticatedImageUrl ? (
                <img 
                  src={authenticatedImageUrl} 
                  alt="Profile" 
                  className="profile-picture"
                />
              ) : (
                <div className="profile-picture-placeholder">
                  {getProfileInitial(profile)}
                </div>
              )}
            </div>
          </div>
          <div className="profile-user-info">
            <h3 className="profile-user-name">
              {profile.firstName} {profile.lastName}
            </h3>
            <div className="profile-user-roles-container">
              {profile.roles && profile.roles.filter(r => r.toUpperCase() !== 'GUEST').length > 0 ? (
                profile.roles
                  .filter(role => role.toUpperCase() !== 'GUEST')
                  .map(role => {
                    const isActiveDashboard = activeDashboardRole
                      && normalizeRole(role) === activeDashboardRole;
                    return (
                    <button 
                      key={role} 
                      type="button"
                      className={`profile-role-switch-btn${isActiveDashboard ? ' profile-role-switch-btn--active' : ''}`}
                      onClick={() => handleRoleSwitch(role)}
                      aria-current={isActiveDashboard ? 'true' : undefined}
                      title={
                        isActiveDashboard
                          ? `Currently in ${formatRoleName(role)} dashboard`
                          : `Switch to ${formatRoleName(role)} dashboard`
                      }
                    >
                      {formatRoleName(role)}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    );
                  })
              ) : (
                <p className="profile-user-role">Guest</p>
              )}
            </div>
            <p className="profile-user-location">
              {profile.phone ? `Phone: ${profile.phone}` : 'No phone number'}
            </p>
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="profile-info-card">
        <div className="profile-info-header">
          <h2 className="profile-section-title">Personal Information</h2>
          <button
            className="profile-edit-btn"
            onClick={() => {
              setShowEditModal(true);
              setUpdateMessage({ text: '', type: '' });
              setProfilePhotoFile(null);
              // Load current profile photo (from profile state or user-specific local storage)
              const userId = profile?.id;
              const storageKey = userId ? getProfilePhotoStorageKey(userId) : null;
              const savedPhotoUrl = storageKey ? localStorage.getItem(storageKey) : null;
              const currentPhotoUrl = profile?.profilePhotoPath || editForm.profilePhotoUrl || savedPhotoUrl || '';
              if (currentPhotoUrl) {
                setProfilePhotoPreview(currentPhotoUrl);
                setEditForm(prev => ({
                  ...prev,
                  profilePhotoUrl: currentPhotoUrl
                }));
              }
            }}
          >
            Edit
          </button>
        </div>
        <div className="profile-info-grid">
          <div className="profile-info-item">
            <label className="profile-info-label">First Name</label>
            <p className="profile-info-value">{profile.firstName || 'N/A'}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">Last Name</label>
            <p className="profile-info-value">{profile.lastName || 'N/A'}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">Email Address</label>
            <p className="profile-info-value">{profile.email || 'N/A'}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">Phone Number</label>
            <p className="profile-info-value">{profile.phone || 'N/A'}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">Date of Birth</label>
            <p className="profile-info-value">{formatDate(profile.dob)}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">User Role</label>
            <p className="profile-info-value">{formatRole(profile.roles)}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">Referral Code</label>
            <p className="profile-info-value">{profile.referralCode || 'N/A'}</p>
          </div>
          <div className="profile-info-item">
            <label className="profile-info-label">KYC Status</label>
            <p className="profile-info-value">
              <span className={`kyc-status-badge kyc-status-${profile.kycStatus?.toLowerCase() || 'pending'}`}>
                {profile.kycStatus || 'PENDING'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="profile-info-card">
        <div className="profile-info-header">
          <h2 className="profile-section-title">Security</h2>
          <button
            className="profile-edit-btn"
            onClick={() => {
              setShowPasswordModal(true);
              setPasswordMessage({ text: '', type: '' });
              setPasswordForm({
                currentPassword: '',
                newPassword: ''
              });
              setShowCurrentPassword(false);
              setShowNewPassword(false);
            }}
          >
            Change Password
          </button>
        </div>
        <div className="profile-info-grid">
          <div className="profile-info-item">
            <label className="profile-info-label">Password</label>
            <p className="profile-info-value">••••••••</p>
          </div>
        </div>
      </div>

      {showDeleteAccount && (
        <div className="profile-info-card profile-account-danger-card">
          <div className="profile-info-header">
            <h2 className="profile-section-title">Account</h2>
            <button
              type="button"
              className="profile-delete-account-btn"
              onClick={handleDeleteAccountNavigation}
            >
              Delete Account
            </button>
          </div>
          <p className="profile-delete-account-hint">
            Deactivate your account permanently. You will be signed out and will not be able to sign in again.
          </p>
        </div>
      )}

      {/* Role Request Section - Only show for non-admin users */}
      {profile && !isSystemAdmin() && (
        <div className="profile-info-card">
          <div className="profile-info-header">
            <h2 className="profile-section-title">Request Additional Roles</h2>
            <button
              className="profile-edit-btn"
              onClick={() => {
                resetRoleRequestModalState();
                setShowRoleRequestModal(true);
              }}
              disabled={isSubmittingRoleRequests}
            >
              Request Roles
            </button>
          </div>

          <div className="role-request-summary">
            {['LANDLORD', 'BNB_HOST', 'AGENT'].map((role) => {
              const summary = getRoleRequestStatusForRole(role);
              const statusText =
                summary.status === 'APPROVED'
                  ? 'Approved'
                  : summary.status === 'PENDING'
                    ? 'Pending review'
                    : summary.status === 'REJECTED'
                      ? summary.canResubmit
                        ? 'Rejected (can resubmit)'
                        : 'Rejected'
                      : 'Not requested';

              return (
                <div key={role} className="role-request-summary-item">
                  <div className="role-request-summary-name">{formatRoleName(role)}</div>
                  <div className={`role-request-summary-pill role-request-summary-pill-${summary.status.toLowerCase()}`}>
                    {statusText}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="role-request-hint">
            Submit multiple role requests so you can access each dashboard using the same email/password. Admin approval is required.
          </div>
        </div>
      )}

      {/* Rejected Role Requests Section - Only show for non-admin users */}
      {profile && !isSystemAdmin() && rejectedRoleRequests.length > 0 && (
        <div className="profile-info-card">
          <div className="profile-info-header">
            <h2 className="profile-section-title">Rejected Role Requests</h2>
          </div>
          <div className="rejected-role-requests-list">
            {rejectedRoleRequests.map((request) => {
              const hasPending = hasPendingRequestForRole(request.role);
              return (
                <div key={request.id} className="rejected-role-request-item">
                  <div className="rejected-role-request-info">
                    <div className="rejected-role-request-header">
                      <span className="rejected-role-name">{formatRoleName(request.role)}</span>
                      <span className="rejected-role-date">Requested: {formatDateLong(request.requestedAt)}</span>
                    </div>
                    {request.rejectionReason && (
                      <div className="rejection-reason-box">
                        <strong>Rejection Reason:</strong>
                        <p>{request.rejectionReason}</p>
                      </div>
                    )}
                    {hasPending && (
                      <div className="profile-message success" style={{ marginTop: '10px', fontSize: '14px' }}>
                        A new request for this role is already pending review.
                      </div>
                    )}
                  </div>
                  <button
                    className="profile-resubmit-btn"
                    onClick={() => handleResubmitClick(request)}
                    disabled={hasPending}
                    title={hasPending ? 'You already have a pending request for this role' : 'Resubmit Request'}
                  >
                    Resubmit Request
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && (
        <Modal onClose={() => setShowEditModal(false)} className="large" ariaLabel="Edit Profile">
          <div className="edit-profile-modal">
            <h2 className="modal-header">Edit Profile</h2>
            
            {updateMessage.text && (
              <div className={`profile-message ${updateMessage.type === 'success' ? 'success' : 'error'}`}>
                {updateMessage.text}
              </div>
            )}

            <form onSubmit={handleUpdateProfile} className="edit-profile-form">
              <div className="profile-photo-section">
                <label className="profile-photo-label">Profile Photo</label>
                <div className="profile-photo-upload">
                  <div className="profile-photo-preview">
                    {authenticatedImageUrl ? (
                      <img src={authenticatedImageUrl} alt="Profile preview" />
                    ) : (
                      <div className="profile-photo-placeholder">
                        {editForm.firstName?.[0]?.toUpperCase() || '👤'}
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    id="profilePhoto"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="profile-photo-input"
                    disabled={isUpdating}
                  />
                  <label htmlFor="profilePhoto" className="profile-photo-btn">
                    {profilePhotoFile ? 'Change Photo' : 'Upload Photo'}
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    required
                    disabled={isUpdating}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    required
                    disabled={isUpdating}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    required
                    disabled={isUpdating}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dob">Date of Birth *</label>
                  <input
                    type="date"
                    id="dob"
                    value={editForm.dob}
                    onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
                    required
                    disabled={isUpdating}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEditModal(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <Modal onClose={() => {
          setShowPasswordModal(false);
          setShowCurrentPassword(false);
          setShowNewPassword(false);
        }} className="medium" ariaLabel="Change Password">
          <div className="edit-profile-modal">
            <h2 className="modal-header">Change Password</h2>
            
            {passwordMessage.text && (
              <div className={`profile-message ${passwordMessage.type === 'success' ? 'success' : 'error'}`}>
                {passwordMessage.text}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="edit-profile-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    required
                    disabled={isChangingPassword}
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    disabled={isChangingPassword}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                  >
                    {showCurrentPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    required
                    disabled={isChangingPassword}
                    placeholder="Enter your new password"
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={isChangingPassword}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={isChangingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Multi Role Request Modal */}
      {showRoleRequestModal && (
        <Modal
          onClose={() => {
            setShowRoleRequestModal(false);
            resetRoleRequestModalState();
          }}
          className="large"
          ariaLabel="Request Roles"
        >
          <div className="edit-profile-modal">
            <h2 className="modal-header">Request Role(s)</h2>

            {roleRequestMessage.text && (
              <div className={`profile-message ${roleRequestMessage.type === 'success' ? 'success' : 'error'}`}>
                {roleRequestMessage.text}
              </div>
            )}

            <form onSubmit={handleRoleRequestsSubmit} className="edit-profile-form">
              <div className="form-group">
                <label>Select one or more roles *</label>
                <div className="role-request-checkboxes">
                  {['LANDLORD', 'BNB_HOST', 'AGENT'].map((role) => {
                    const selectable = isSelectableRoleRequest(role);
                    const summary = getRoleRequestStatusForRole(role);

                    return (
                      <label
                        key={role}
                        className={`role-request-checkbox ${!selectable ? 'role-request-checkbox-disabled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={roleSelections.includes(role)}
                          disabled={!selectable || isSubmittingRoleRequests}
                          onChange={() => {
                            if (!selectable) return;
                            setRoleSelections((prev) => {
                              if (prev.includes(role)) return prev.filter((r) => r !== role);
                              return [...prev, role];
                            });
                          }}
                        />
                        <span className="role-request-checkbox-label">{formatRoleName(role)}</span>
                        <span className="role-request-checkbox-status">
                          {summary.status === 'APPROVED'
                            ? 'Approved'
                            : summary.status === 'PENDING'
                              ? 'Pending'
                              : summary.status === 'REJECTED'
                                ? summary.canResubmit
                                  ? 'Rejected (can resubmit)'
                                  : 'Rejected'
                                : 'Not requested'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>


              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowRoleRequestModal(false);
                    resetRoleRequestModalState();
                  }}
                  disabled={isSubmittingRoleRequests}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isSubmittingRoleRequests}>
                  {isSubmittingRoleRequests ? 'Submitting...' : 'Submit Role Requests'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Resubmit Role Request Modal */}
      {showResubmitModal && selectedRoleRequest && (
        <Modal 
          onClose={() => {
            setShowResubmitModal(false);
            setSelectedRoleRequest(null);
            setResubmitMessage({ text: '', type: '' });
          }} 
          className="large" 
          ariaLabel="Resubmit Role Request"
        >
          <div className="edit-profile-modal">
            <h2 className="modal-header">Resubmit Role Request</h2>
            
            {resubmitMessage.text && (
              <div className={`profile-message ${resubmitMessage.type === 'success' ? 'success' : 'error'}`}>
                {resubmitMessage.text}
              </div>
            )}

            <div className="resubmit-info-box">
              <p><strong>Previous Request:</strong> {formatRoleName(selectedRoleRequest.role)}</p>
              {selectedRoleRequest.rejectionReason && (
                <div className="rejection-reason-display">
                  <strong>Rejection Reason:</strong>
                  <p>{selectedRoleRequest.rejectionReason}</p>
                </div>
              )}
            </div>

            <form onSubmit={handleResubmit} className="edit-profile-form">
              <div className="form-group">
                <label htmlFor="resubmitRole">Role to Request *</label>
                <select
                  id="resubmitRole"
                  value={resubmitRole}
                  onChange={(e) => {
                    setResubmitRole(e.target.value);
                  }}
                  disabled={isResubmitting}
                >
                  <option value="">Select a role</option>
                  <option value="LANDLORD">Landlord</option>
                  <option value="BNB_HOST">BNB Host</option>
                  <option value="AGENT">Agent</option>
                </select>
              </div>


              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowResubmitModal(false);
                    setSelectedRoleRequest(null);
                    setResubmitMessage({ text: '', type: '' });
                  }}
                  disabled={isResubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isResubmitting}
                >
                  {isResubmitting ? 'Submitting...' : 'Resubmit Request'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Profile;
